import React, { useState, useEffect } from 'react';
import { 
  Scale, 
  Ruler, 
  RotateCcw,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Info,
  Calendar,
  User,
  Phone,
  MapPin,
  X,
  CheckCircle2,
  Stethoscope,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

const App = () => {
  /* ===== CONFIG & STATE ===== */
  const params = new URLSearchParams(window.location.search);
  const source  = params.get('source') || 'youmed_article';
  const postId  = params.get('post_id') || 'unknown';
  const postUrl = params.get('post_url') || 'unknown';
  const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzsIWWkwQjjpv3FPj4FnnH6Se63vWGY77340XQDyLBh3P8o8defag_VJqDywRJwQgQ/exec";

  const [gender, setGender] = useState('male'); 
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showConsultPopup, setShowConsultPopup] = useState(false);
  const [isBmiEventSent, setIsBmiEventSent] = useState(false);
  const [isConsultSubmitted, setIsConsultSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    province: ''
  });

  useEffect(() => {
    // Add viewport meta via JS to ensure mobile responsiveness
    const meta = document.createElement('meta');
    meta.name = "viewport";
    meta.content = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0";
    document.getElementsByTagName('head')[0].appendChild(meta);

    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  const statusConfig = {
    0: { color: "#3b82f6", bg: "bg-blue-50", border: "border-blue-100", header: "bg-blue-600", text: "text-blue-600" },
    1: { color: "#10b981", bg: "bg-emerald-50", border: "border-emerald-100", header: "bg-[#009a61]", text: "text-emerald-600" },
    2: { color: "#f59e0b", bg: "bg-amber-50", border: "border-amber-100", header: "bg-amber-500", text: "text-amber-600" },
    3: { color: "#f97316", bg: "bg-orange-50", border: "border-orange-100", header: "bg-orange-500", text: "text-orange-600" },
    4: { color: "#ef4444", bg: "bg-red-50", border: "border-red-100", header: "bg-red-600", text: "text-red-600" }
  };

  useEffect(() => {
    if (window.self !== window.top) {
      document.documentElement.setAttribute('data-embed', 'true');
    }
  }, []);

  const genderTheme = {
    male: { header: "bg-blue-600", btn: "bg-blue-600", hover: "hover:bg-blue-700", ring: "focus:border-blue-500" },
    female: { header: "bg-rose-500", btn: "bg-rose-500", hover: "hover:bg-rose-600", ring: "focus:border-rose-500" }
  };

  const sendBMILog = async (bmiResult) => {
    try {
      const payload = {
        name: "N/A",
        phone: "",
        age: parseInt(age) || "",
        gender: gender === "male" ? "Nam" : "Nữ",
        location: "",
        height: parseFloat(height) || "",
        weight: parseFloat(weight) || "",
        bmi: bmiResult.bmi,
        bmi_category: bmiResult.category,
        goal: bmiResult.bmi < 18.5 ? "Tăng cân" : "Giảm cân",
        concern: bmiResult.risk,
        source: source || "bmi_adult_tool",
        post_id: postId,
        post_url: postUrl,
      };

      await fetch(SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload)
      });
    } catch (e) {
      console.error("BMI auto-log failed", e);
    }
  };

  const calculateBMI = () => {
    setError('');
    const a = parseInt(age);
    const w = parseFloat(weight);
    const hCm = parseFloat(height);

    if (!a || !w || !hCm) {
      setError('Vui lòng nhập đầy đủ thông tin');
      return;
    }
    if (a < 18) {
      setError('kid');
      return;
    }

    const h = hCm / 100;
    if (!h || h <= 0) {
      setError('Dữ liệu không hợp lệ');
      return;
    }

    const bmiVal = parseFloat((w / (h * h)).toFixed(1));
    const heightSpare = hCm - 100;
    const idealW = ((heightSpare * 9) / 10).toFixed(1);

    let category = "";
    let risk = "";
    let message = "";
    let statusId = 1;

    if (bmiVal < 18.5) {
      category = "Cân nặng thấp (Gầy)";
      risk = "Nguy cơ thiếu chất";
      message = "Bạn hơi gầy một chút, hãy chú ý bồi bổ thêm nhé!";
      statusId = 0;
    } else if (bmiVal <= 22.9) {
      category = "Dáng người cân đối";
      risk = "Rất tốt";
      message = "Tuyệt vời! Bạn đang sở hữu chỉ số hình thể rất tốt.";
      statusId = 1;
    } else if (bmiVal <= 24.9) {
      category = "Thừa cân nhẹ";
      risk = "Cần chú ý";
      message = "Bạn hơi thừa cân một chút, nên vận động nhẹ nhàng hơn.";
      statusId = 2;
    } else if (bmiVal <= 29.9) {
      category = "Béo phì độ 1";
      risk = "Nguy cơ cao";
      message = "Bạn nên điều chỉnh lại chế độ ăn uống.";
      statusId = 3;
    } else {
      category = "Béo phì độ 2";
      risk = "Nguy hiểm";
      message = "Hãy tham khảo ý kiến bác sĩ.";
      statusId = 4;
    }

    const bmiResult = { bmi: bmiVal, category, idealW, message, risk, statusId };
    setResult(bmiResult);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'bmi_result', { source, post_id: postId, post_url: postUrl, bmi_status: statusId, tool: 'bmi' });
      if (!isBmiEventSent) {
        window.gtag('event', 'bmi_submit', { source, post_id: postId, post_url: postUrl, tool: 'bmi' });
        setIsBmiEventSent(true);
      }
    }
    sendBMILog(bmiResult);
    // Scroll to top of results on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.fullName,
        phone: formData.phone,
        age: parseInt(age),
        gender: gender === "male" ? "Nam" : "Nữ",
        location: formData.province,
        height,
        weight,
        bmi: result?.bmi,
        bmi_category: result?.category,
        goal: result?.bmi < 18.5 ? "Tăng cân" : "Giảm cân",
        concern: result?.risk,
        source: source || "bmi_adult_tool",
        post_id: postId,
        post_url: postUrl
      };

      await fetch(SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload)
      });

      setIsConsultSubmitted(true);
      setTimeout(() => {
        setShowConsultPopup(false);
        setIsConsultSubmitted(false);
        setFormData({ fullName: '', phone: '', province: '' });
      }, 2500);
    } catch (err) {
      console.error("Submit form failed", err);
    }
  };

  const currentTheme = result ? statusConfig[result.statusId] : { header: genderTheme[gender].header };
  const isUnderweight = result?.bmi < 18.5;
  const needsConsult = result && (result.bmi < 18.5 || result.bmi >= 23);

  return (
    <div className="w-full min-h-screen bg-slate-50 flex flex-col md:items-center md:justify-center p-0 md:p-4 font-['Roboto',sans-serif]">
      {/* Mobile-friendly container: No rounded corners on mobile, full width */}
      <div className="w-full max-w-[750px] bg-white md:rounded-[2.5rem] shadow-none md:shadow-2xl border-none md:border md:border-slate-100 overflow-hidden transition-all duration-500 relative flex-1 md:flex-none">
        
        {/* Header - Tăng độ tương phản và diện tích chạm */}
        <div className={`${currentTheme.header} px-6 py-8 md:px-8 md:py-10 text-white flex justify-between items-center transition-colors duration-500 relative`}>
          <div className="relative z-10">
            <h1 className="text-xl md:text-2xl font-black tracking-tight uppercase">Kiểm tra chỉ số cơ thể</h1>
            <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-widest opacity-90 mt-1 flex items-center gap-2">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              Theo chuẩn Châu Á (IDI & WPRO)
            </p>
          </div>
          <div className="bg-white/20 p-2.5 rounded-xl border border-white/20 backdrop-blur-md">
            <ShieldCheck size={24} strokeWidth={2.5} />
          </div>
        </div>

        <div className="p-6 md:p-8">
          {!result ? (
            <div className="space-y-6 md:space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* Giới tính - Nút lớn dễ bấm */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Giới tính</label>
                <div className="flex p-1.5 bg-slate-100 rounded-[1.25rem] gap-1.5">
                  <button 
                    onClick={() => setGender('male')}
                    className={`flex-1 py-3.5 rounded-xl text-[13px] font-black transition-all flex items-center justify-center gap-2 ${gender === 'male' ? 'bg-white shadow-sm text-blue-600 scale-[1.01]' : 'text-slate-400'}`}
                  >
                    NAM GIỚI
                  </button>
                  <button 
                    onClick={() => setGender('female')}
                    className={`flex-1 py-3.5 rounded-xl text-[13px] font-black transition-all flex items-center justify-center gap-2 ${gender === 'female' ? 'bg-white shadow-sm text-rose-500 scale-[1.01]' : 'text-slate-400'}`}
                  >
                    NỮ GIỚI
                  </button>
                </div>
              </div>

              {/* Input grid - 1 cột trên mobile */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Tuổi</label>
                  <div className="relative group">
                    <User className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${error ? 'text-red-400' : 'text-slate-300 group-focus-within:text-slate-500'}`} />
                    <input 
                      type="number"
                      inputMode="numeric"
                      value={age}
                      onChange={(e) => { setAge(e.target.value); setError(''); }}
                      placeholder="25"
                      className={`w-full pl-12 pr-5 py-4 bg-slate-50 border-2 rounded-[1rem] text-base font-bold focus:bg-white outline-none transition-all ${error === 'Vui lòng nhập đầy đủ thông tin' ? 'border-red-200' : 'border-transparent'} ${genderTheme[gender].ring}`}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Chiều cao (cm)</label>
                  <div className="relative group">
                    <Ruler className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 group-focus-within:text-slate-500 transition-colors" />
                    <input 
                      type="number"
                      inputMode="decimal"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      placeholder="165"
                      className={`w-full pl-12 pr-5 py-4 bg-slate-50 border-2 border-transparent rounded-[1rem] text-base font-bold focus:bg-white outline-none transition-all ${genderTheme[gender].ring}`}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Cân nặng (kg)</label>
                  <div className="relative group">
                    <Scale className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 group-focus-within:text-slate-500 transition-colors" />
                    <input 
                      type="number"
                      inputMode="decimal"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="58"
                      className={`w-full pl-12 pr-5 py-4 bg-slate-50 border-2 border-transparent rounded-[1rem] text-base font-bold focus:bg-white outline-none transition-all ${genderTheme[gender].ring}`}
                    />
                  </div>
                </div>
              </div>

              {error === 'kid' ? (
                <div className="bg-blue-50 border border-blue-100 p-5 rounded-2xl space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex items-start gap-3 text-blue-700">
                    <AlertCircle size={24} className="shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-black uppercase tracking-tight mb-1">Dành cho trẻ em</p>
                      <p className="text-xs font-medium leading-relaxed opacity-80">
                        Chỉ số BMI cho người dưới 18 tuổi cần so sánh với biểu đồ tăng trưởng của WHO.
                      </p>
                    </div>
                  </div>
                  <a 
                    href="https://youmed.vn/tin-tuc/do-bmi-tre-em/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-4 bg-blue-600 text-white rounded-xl font-black text-[11px] uppercase tracking-widest shadow-lg shadow-blue-200"
                  >
                    Xem BMI trẻ em <ExternalLink size={14} />
                  </a>
                </div>
              ) : error && (
                <div className="bg-red-50 border border-red-100 p-4 rounded-xl flex items-center gap-3 text-red-600">
                  <AlertCircle size={20} className="shrink-0" />
                  <p className="text-sm font-bold">{error}</p>
                </div>
              )}
              
              <button 
                onClick={calculateBMI}
                className={`w-full py-5 text-white rounded-[1rem] font-black text-sm uppercase tracking-[0.2em] shadow-xl active:scale-[0.97] transition-all touch-manipulation ${genderTheme[gender].btn} ${genderTheme[gender].hover}`}
              >
                Phân tích ngay
              </button>
            </div>
          ) : (
            <div className="animate-in fade-in zoom-in-95 duration-500 space-y-6">
              {/* BMI Result Card - Tối ưu kích thước chữ cho mobile */}
              <div className={`${statusConfig[result.statusId].bg} ${statusConfig[result.statusId].border} rounded-[2rem] p-6 md:p-10 text-center border-2 shadow-inner`}>
                <p className={`text-[10px] font-black uppercase tracking-[0.3em] ${statusConfig[result.statusId].text} mb-3 opacity-70`}>BMI của {age} tuổi</p>
                <div className="flex justify-center items-baseline gap-1 mb-2">
                  <span className={`text-6xl md:text-8xl font-black tracking-tighter ${statusConfig[result.statusId].text}`}>{result.bmi}</span>
                  <span className={`text-lg font-bold ${statusConfig[result.statusId].text} opacity-40`}>kg/m²</span>
                </div>
                <div className={`inline-flex items-center gap-2 px-6 py-2 bg-white rounded-full shadow-sm ${statusConfig[result.statusId].text} font-black text-lg mb-4 border border-current border-opacity-5`}>
                  {result.category}
                </div>
                <p className="text-slate-600 text-[14px] leading-relaxed font-semibold italic opacity-90">
                  "{result.message}"
                </p>
              </div>

              {/* Stats - Xếp chồng 1 cột trên mobile */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-5 rounded-[1.25rem] border border-slate-100 flex justify-between items-center">
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Cân nặng mục tiêu</p>
                    <p className="text-2xl font-black text-slate-700">{result.idealW} <span className="text-xs opacity-30 uppercase">kg</span></p>
                  </div>
                  <div className="bg-emerald-100 p-2.5 rounded-lg">
                    <Info className="text-emerald-600" size={18} />
                  </div>
                </div>
                <div className={`p-5 rounded-[1.25rem] border flex justify-between items-center ${statusConfig[result.statusId].bg} ${statusConfig[result.statusId].border}`}>
                  <div>
                    <p className={`text-[9px] font-black ${statusConfig[result.statusId].text} opacity-60 uppercase tracking-widest`}>Rủi ro bệnh tật</p>
                    <p className={`text-xl font-black ${statusConfig[result.statusId].text}`}>{result.risk}</p>
                  </div>
                  <div className={`${statusConfig[result.statusId].text} opacity-20`}>
                    <ShieldCheck size={28} />
                  </div>
                </div>
              </div>

              {/* Action Buttons - Sticky feeling on mobile */}
              <div className="flex flex-col gap-3">
                {needsConsult && (
                  <button 
                    onClick={() => setShowConsultPopup(true)}
                    className={`w-full py-5 text-white rounded-xl font-black text-sm uppercase tracking-widest shadow-lg active:scale-[0.97] flex items-center justify-center gap-2 ${isUnderweight ? 'bg-blue-600' : 'bg-red-600'} animate-bounce-subtle`}
                  >
                    <Stethoscope size={18} /> {isUnderweight ? 'Tư vấn tăng cân' : 'Tư vấn giảm cân'}
                  </button>
                )}
                <button 
                  onClick={() => { setResult(null); setAge(''); setError(''); window.scrollTo(0,0); }}
                  className="w-full py-4 bg-slate-100 text-slate-600 rounded-xl font-black text-[12px] uppercase tracking-widest flex items-center justify-center gap-2"
                >
                  <RotateCcw size={16} /> Kiểm tra lại
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Disclaimer - Tối ưu cho màn hình nhỏ */}
        <div className="bg-slate-50/50 border-t border-slate-100 px-6 py-6 pb-10 md:pb-6">
          <div className="flex items-start gap-3">
            <div className="bg-slate-200 p-2 rounded-lg shrink-0">
              <Info className="w-3.5 h-3.5 text-slate-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowDisclaimer(!showDisclaimer)}>
                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
                  Lưu ý quan trọng
                </h4>
                {showDisclaimer ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
              </div>
              
              {showDisclaimer && (
                <div className="mt-4 text-[12px] leading-relaxed text-slate-500 font-medium animate-in slide-in-from-top-2 duration-300">
                  <p className="mb-4 text-red-500/80 font-bold bg-red-50 p-3 rounded-lg border border-red-100">
                    * Chỉ số BMI này chỉ dành cho người trưởng thành (≥18 tuổi). Không áp dụng với trẻ em, phụ nữ mang thai, vận động viên chuyên nghiệp hoặc người cao tuổi suy nhược. Kết quả chỉ mang tính tham khảo, không thay thế cho chẩn đoán tại cơ sở y tế..
                  </p>
                  <div className="bg-white p-4 rounded-xl border border-slate-200">
                    <p className="text-slate-600 mb-4 text-center">
                     Nếu có nhu cầu chẩn đoán chính xác tình trạng cơ thể và sức khỏe, hãy đặt lịch khám nhanh chóng tại app Youmed.
                    </p>
                    <a 
                      href="https://bit.ly/4tbwM2W" 
                      className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#009a61] text-white text-[10px] font-black uppercase tracking-widest rounded-lg"
                    >
                      <Calendar size={14} /> Đặt lịch ngay
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CONSULT POPUP - Tối ưu Modal cho Mobile */}
        {showConsultPopup && (
          <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
            {/* Slide up from bottom on mobile */}
            <div className="w-full max-w-[450px] bg-white rounded-t-[2rem] md:rounded-[2rem] shadow-2xl overflow-hidden relative animate-in slide-in-from-bottom md:zoom-in-95 duration-300 max-h-[90vh] flex flex-col">
              <button onClick={() => setShowConsultPopup(false)} className="absolute top-4 right-4 p-2 text-slate-400 bg-slate-50 rounded-full z-20"><X size={20} /></button>
              
              <div className="overflow-y-auto p-6 pt-10">
                {!isConsultSubmitted  ? (
                  <>
                    <div className="text-center mb-6">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 ${isUnderweight ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'}`}>
                        <Stethoscope size={28} />
                      </div>
                      <h2 className="text-xl font-black text-slate-800 tracking-tight mb-1">Tư vấn miễn phí</h2>
                      <p className="text-slate-500 text-[13px] font-medium leading-relaxed">
                        Để lại thông tin, chuyên gia Youmed sẽ liên hệ hỗ trợ bạn sớm nhất.
                      </p>
                    </div>
                    <form onSubmit={handleFormSubmit} className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Họ và tên</label>
                        <input required type="text" value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} placeholder="Nguyễn Văn A" className="w-full px-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-xl text-base font-bold focus:bg-white focus:border-emerald-500 outline-none transition-all" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Số điện thoại</label>
                        <input required type="tel" inputMode="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="0901 234 567" className="w-full px-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-xl text-base font-bold focus:bg-white focus:border-emerald-500 outline-none transition-all" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Tỉnh thành</label>
                        <input required type="text" value={formData.province} onChange={(e) => setFormData({...formData, province: e.target.value})} placeholder="TP. Hồ Chí Minh" className="w-full px-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-xl text-base font-bold focus:bg-white focus:border-emerald-500 outline-none transition-all" />
                      </div>
                      <button type="submit" className={`w-full py-4 mt-2 text-white rounded-xl font-black text-sm uppercase tracking-widest shadow-xl active:scale-[0.97] ${isUnderweight ? 'bg-blue-600' : 'bg-red-600'}`}>Gửi thông tin</button>
                    </form>
                  </>
                ) : (
                  <div className="py-10 text-center">
                    <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-5"><CheckCircle2 size={40} /></div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tight mb-2">Gửi thành công!</h2>
                    <p className="text-slate-500 text-[13px] font-medium">Chúng tôi sẽ liên hệ với bạn sớm.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .animate-bounce-subtle { animation: bounce-subtle 2s infinite ease-in-out; }
        
        /* Chống zoom trên mobile cho iOS */
        @media screen and (max-width: 768px) {
          input, select, textarea {
            font-size: 16px !important;
          }
        }

        /* Tối ưu scrollbar cho modal */
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default App;